import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock } from '../../assets/icons';
import { Stats } from '../../assets/icons';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import '../../assets/css/doctor/doctor_dashboard.css';
import { casesApi } from '../../network/apis/cases';
import VisitorStats from '../../components/elements/visitorStats';
import RoleSwitcher from '../../components/common/RoleSwitcher';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const DoctorDashboard = () => {
    const navigate = useNavigate();
    const chartRef = useRef(null);
    
    // State management
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [user] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    });

    // Memoized fetch function
    const fetchCases = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await casesApi.getCases();
            
            // Handle paginated response
            const casesData = response?.data?.data || response?.data || [];
            setCases(Array.isArray(casesData) ? casesData : []);
        } catch (err) {
            console.error('Failed to fetch cases:', err);
            setError(err.displayMessage || err.message || 'Failed to fetch cases');
            setCases([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCases();
    }, [fetchCases]);

    // Calculate real statistics from cases data
    const statsData = React.useMemo(() => {
        const total = cases.length;
        const statusCounts = cases.reduce((acc, c) => {
            const status = (c.status || '').toUpperCase();
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        const priorityCounts = cases.reduce((acc, c) => {
            const priority = (c.priority || '').toUpperCase();
            acc[priority] = (acc[priority] || 0) + 1;
            return acc;
        }, {});

        return {
            total,
            pending: statusCounts['PENDING'] || 0,
            assigned: statusCounts['ASSIGNED'] || 0,
            inProgress: statusCounts['IN_PROGRESS'] || 0,
            resolved: statusCounts['RESOLVED'] || 0,
            cancelled: statusCounts['CANCELLED'] || 0,
            highPriority: priorityCounts['HIGH'] || 0,
            mediumPriority: priorityCounts['MEDIUM'] || 0,
            lowPriority: priorityCounts['LOW'] || 0,
            // Active cases = assigned + in progress
            active: (statusCounts['ASSIGNED'] || 0) + (statusCounts['IN_PROGRESS'] || 0) + (statusCounts['PENDING'] || 0),
            // Concluded = resolved + cancelled
            concluded: (statusCounts['RESOLVED'] || 0) + (statusCounts['CANCELLED'] || 0)
        };
    }, [cases]);

    // Calculate response rate (resolved / total excluding pending)
    const responseRate = React.useMemo(() => {
        const handledCases = statsData.total - statsData.pending;
        if (handledCases === 0) return 0;
        return Math.round((statsData.resolved / handledCases) * 100);
    }, [statsData]);

    // Chart data based on real case statistics
    const chartData = React.useMemo(() => ({
        labels: ['Resolved', 'Pending', 'In Progress', 'Assigned'],
        datasets: [{
            data: [
                statsData.resolved,
                statsData.pending,
                statsData.inProgress,
                statsData.assigned
            ],
            backgroundColor: ['#4CAF50', '#FF9800', '#2196F3', '#9C27B0'],
            hoverBackgroundColor: ['#45a049', '#f57c00', '#1976D2', '#7B1FA2'],
            borderWidth: 0
        }]
    }), [statsData]);

    // Recent cases for quick view
    const recentCases = React.useMemo(() => {
        return [...cases]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5);
    }, [cases]);

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffHours < 1) return 'Just now';
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        return date.toLocaleDateString();
    };

    // Loading state
    if (loading) {
        return (
            <div className="doctor-dashboard">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="doctor-dashboard">
                <div className="error-container">
                    <h3>Error Loading Dashboard</h3>
                    <p>{error}</p>
                    <button onClick={fetchCases} className="retry-btn">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="doctor-dashboard">
            <header className="dashboard-header">
                <div className="welcome-section">
                    <h1>Welcome, Dr. {user.first_name || 'Doctor'}</h1>
                    <p className="welcome-subtitle">
                        {new Date().toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        })}
                    </p>
                </div>
                
                <div className="header-actions">
                    <RoleSwitcher />
                </div>
            </header>

            <div className="dashboard-stats">
                <div className="stat-card" onClick={() => navigate('/vet/cases')}>
                    <div className="stat-icon">
                        <img src={Stats} alt="Total Cases" />
                    </div>
                    <div className="stat-content">
                        <h4>Assigned Cases</h4>
                        <span className="stat-number">{statsData.total}</span>
                        <span className="stat-label">Total cases assigned to you</span>
                    </div>
                </div>
                
                <div className="stat-card highlight" onClick={() => navigate('/vet/cases?status=active')}>
                    <div className="stat-icon">
                        <img src={Stats} alt="Active Cases" />
                    </div>
                    <div className="stat-content">
                        <h4>Active Cases</h4>
                        <span className="stat-number">{statsData.active}</span>
                        <span className="stat-label">Currently being handled</span>
                    </div>
                </div>
                
                <div className="stat-card" onClick={() => navigate('/vet/cases?status=resolved')}>
                    <div className="stat-icon">
                        <img src={Stats} alt="Concluded Cases" />
                    </div>
                    <div className="stat-content">
                        <h4>Concluded Cases</h4>
                        <span className="stat-number">{statsData.concluded}</span>
                        <span className="stat-label">Resolved or closed</span>
                    </div>
                </div>
                
                <div className="stat-card highlight">
                    <div className="stat-icon">
                        <img src={Clock} alt="Response Rate" />
                    </div>
                    <div className="stat-content">
                        <h4>Success Rate</h4>
                        <span className="stat-number">{responseRate}%</span>
                        <span className="stat-label">Cases resolved successfully</span>
                    </div>
                </div>
            </div>

            <div className="platform-activity">
                <h3>Platform Activity</h3>
                <VisitorStats variant="standard" />
            </div>
                
            <div className="summary-section">
                <div className="summary-grid">
                    <div className="summary-table">
                        <h3>Case Summary</h3>
                        {statsData.total === 0 ? (
                            <div className="empty-state">
                                <p>No cases assigned yet</p>
                                <span>Cases assigned to you will appear here</span>
                            </div>
                        ) : (
                            <table>
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Count</th>
                                        <th>Percentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <span className="status-dot pending"></span>
                                            Pending
                                        </td>
                                        <td>{statsData.pending}</td>
                                        <td>{statsData.total > 0 ? Math.round((statsData.pending / statsData.total) * 100) : 0}%</td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <span className="status-dot assigned"></span>
                                            Assigned
                                        </td>
                                        <td>{statsData.assigned}</td>
                                        <td>{statsData.total > 0 ? Math.round((statsData.assigned / statsData.total) * 100) : 0}%</td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <span className="status-dot in-progress"></span>
                                            In Progress
                                        </td>
                                        <td>{statsData.inProgress}</td>
                                        <td>{statsData.total > 0 ? Math.round((statsData.inProgress / statsData.total) * 100) : 0}%</td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <span className="status-dot resolved"></span>
                                            Resolved
                                        </td>
                                        <td>{statsData.resolved}</td>
                                        <td>{statsData.total > 0 ? Math.round((statsData.resolved / statsData.total) * 100) : 0}%</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="summary-chart">
                        <h3>Status Distribution</h3>
                        {statsData.total === 0 ? (
                            <div className="empty-state chart-empty">
                                <p>No data to display</p>
                            </div>
                        ) : (
                            <div className="chart-container">
                                <Doughnut 
                                    ref={chartRef}
                                    key="doctor-dashboard-chart"
                                    data={chartData}
                                    options={{
                                        cutout: '70%',
                                        responsive: true,
                                        maintainAspectRatio: true,
                                        plugins: {
                                            legend: {
                                                position: 'bottom',
                                                labels: {
                                                    boxWidth: 12,
                                                    padding: 15,
                                                    font: {
                                                        size: 12
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Recent Cases Section */}
            {recentCases.length > 0 && (
                <div className="recent-cases-section">
                    <div className="section-header">
                        <h3>Recent Cases</h3>
                        <button 
                            className="view-all-btn"
                            onClick={() => navigate('/vet/cases')}
                        >
                            View All
                        </button>
                    </div>
                    <div className="recent-cases-list">
                        {recentCases.map((caseItem) => (
                            <div 
                                key={caseItem.id} 
                                className="recent-case-card"
                                onClick={() => navigate(`/vet/cases/${caseItem.id}`)}
                            >
                                <div className="case-info">
                                    <h4>{caseItem.title || 'Untitled Case'}</h4>
                                    <p className="case-farmer">
                                        {caseItem.farmer_name || 'Unknown Farmer'}
                                    </p>
                                    <p className="case-code">#{caseItem.case_code || caseItem.id}</p>
                                </div>
                                <div className="case-meta">
                                    <span className={`status-badge ${(caseItem.status || '').toLowerCase().replace('_', '-')}`}>
                                        {(caseItem.status || 'Unknown').replace('_', ' ')}
                                    </span>
                                    <span className={`priority-badge ${(caseItem.priority || '').toLowerCase()}`}>
                                        {caseItem.priority || 'Normal'}
                                    </span>
                                    <span className="case-time">
                                        {formatDate(caseItem.created_at)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DoctorDashboard;